<template>
  <section class="py-20 px-6 bg-gradient-to-br from-soft-white to-lavender-tint/30" ref="sectionRef">
    <div class="container mx-auto max-w-7xl">
      <!-- Section Header -->
      <div
        class="text-center mb-16"
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :visible-once="{ opacity: 1, y: 0 }"
        :duration="800"
      >
        <h2 class="text-4xl md:text-5xl font-poppins font-semibold text-dark-gray mb-6">
          Our <span class="text-light-purple">Services</span>
        </h2>
        <div class="w-24 h-1 bg-light-purple mx-auto rounded-full mb-8"></div>
        <p class="text-lg text-dark-gray/80 max-w-3xl mx-auto leading-relaxed">
          Delivering healthcare excellence through innovative solutions tailored for both individual and corporate needs.
        </p>
      </div>

      <!-- Services Grid -->
      <div class="grid lg:grid-cols-2 gap-8 mb-16">
        <div
          v-for="(service, index) in services"
          :key="index"
          class="card-neumorphic group relative overflow-hidden cursor-pointer"
          v-motion
          :initial="{ opacity: 0, y: 50 }"
          :visible-once="{ opacity: 1, y: 0 }"
          :duration="800"
          :delay="service.delay"
          @mouseenter="onServiceHover"
          @mouseleave="onServiceLeave"
        >
          <!-- Background Gradient -->
          <div :class="`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-neumorphic`"></div>
          
          <div class="relative z-10">
            <!-- Icon Container -->
            <div
              class="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-neumorphic flex items-center justify-center shadow-soft transition-transform duration-500"
              :ref="`iconRef${index}`"
            >
              <component :is="service.icon" :size="40" class="text-light-purple" />
            </div>

            <!-- Content -->
            <div class="text-center mb-8">
              <h3 class="text-2xl md:text-3xl font-poppins font-semibold text-dark-gray mb-4">
                {{ service.title }}
              </h3>
              <p class="text-dark-gray/70 text-lg leading-relaxed mb-6">
                {{ service.subtitle }}
              </p>
            </div>

            <!-- Features List -->
            <div class="space-y-3">
              <div
                v-for="(feature, featureIndex) in service.features"
                :key="featureIndex"
                class="flex items-center space-x-3"
                v-motion
                :initial="{ opacity: 0, x: -20 }"
                :visible-once="{ opacity: 1, x: 0 }"
                :duration="500"
                :delay="service.delay + 100 * featureIndex"
              >
                <div class="w-2 h-2 bg-light-purple rounded-full flex-shrink-0"></div>
                <span class="text-dark-gray/70">{{ feature }}</span>
              </div>
            </div>

            <!-- CTA Button -->
            <div
              class="mt-8 text-center"
              v-motion
              :initial="{ opacity: 0 }"
              :visible-once="{ opacity: 1 }"
              :duration="500"
              :delay="service.delay + 500"
            >
              <button
                class="btn-neumorphic text-light-purple hover:text-white hover:bg-light-purple group/btn relative overflow-hidden"
                @mouseenter="onButtonHover"
                @mouseleave="onButtonLeave"
                @click="openLearnMoreModal(service.title.toLowerCase().includes('corporate') ? 'corporate' : 'home')"
              >
                <span class="relative z-10">Learn More</span>
                <div
                  class="absolute inset-0 bg-light-purple transition-all duration-300 opacity-0 group-hover/btn:opacity-100"
                  style="border-radius: 24px"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Benefits Section -->
      <div
        class="text-center"
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :visible-once="{ opacity: 1, y: 0 }"
        :duration="800"
        :delay="600"
      >
        <h3 class="text-2xl font-poppins font-semibold text-dark-gray mb-8">
          Why Choose Homvisit?
        </h3>
        
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div
            v-for="(benefit, index) in benefits"
            :key="index"
            class="text-center cursor-pointer"
            v-motion
            :initial="{ opacity: 0, scale: 0.8 }"
            :visible-once="{ opacity: 1, scale: 1 }"
            :duration="500"
            :delay="800 + index * 100"
            @mouseenter="onBenefitHover"
            @mouseleave="onBenefitLeave"
          >
            <div class="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-neumorphic flex items-center justify-center shadow-soft">
              <component :is="benefit.icon" :size="24" class="text-light-purple" />
            </div>
            <p class="text-dark-gray/70 text-sm font-medium">{{ benefit.text }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref } from 'vue'
import { Building2, Home, Users, Clock, Shield, Award } from 'lucide-vue-next'

export default {
  name: 'ServicesSection',
  components: {
    Building2,
    Home,
    Users,
    Clock,
    Shield,
    Award
  },
  setup() {
    const sectionRef = ref(null)

    const services = [
      {
        icon: Building2,
        title: 'Corporate Medical Camps',
        subtitle: 'Comprehensive on-site diagnostics for employee wellness.',
        features: [
          'On-site health screenings',
          'Bulk diagnostic packages',
          'Employee wellness programs',
          'Health reports & analytics'
        ],
        color: 'from-purple-400 to-purple-600',
        delay: 200
      },
      {
        icon: Home,
        title: 'Home Blood Collection',
        subtitle: 'Secondary service — doorstep collection when individuals need it.',
        features: [
          'Doorstep sample collection',
          'Certified phlebotomists',
          'Safe & hygienic procedures',
          'Available alongside corporate work'
        ],
        color: 'from-blue-400 to-blue-600',
        delay: 400
      }
    ]

    const benefits = [
      { icon: Clock, text: 'Quick & Convenient' },
      { icon: Shield, text: 'Safe & Reliable' },
      { icon: Award, text: 'Certified Professionals' },
      { icon: Users, text: 'Trusted by Thousands' }
    ]

    const onServiceHover = (e) => {
      e.currentTarget.style.transform = 'translateY(-10px) rotateX(5deg)'
      const icon = e.currentTarget.querySelector('.w-24')
      if (icon) icon.style.transform = 'scale(1.1) rotate(360deg)'
    }

    const onServiceLeave = (e) => {
      e.currentTarget.style.transform = 'translateY(0px) rotateX(0deg)'
      const icon = e.currentTarget.querySelector('.w-24')
      if (icon) icon.style.transform = 'scale(1) rotate(0deg)'
    }

    const onButtonHover = (e) => {
      e.target.style.transform = 'scale(1.05)'
    }

    const onButtonLeave = (e) => {
      e.target.style.transform = 'scale(1)'
    }

    const onBenefitHover = (e) => {
      e.currentTarget.style.transform = 'scale(1.05)'
    }

    const onBenefitLeave = (e) => {
      e.currentTarget.style.transform = 'scale(1)'
    }

    const openLearnMoreModal = (serviceType) => {
      // Emit event to parent component to open learn more modal
      window.dispatchEvent(new CustomEvent('openLearnMoreModal', { detail: serviceType }))
    }

    return {
      sectionRef,
      services,
      benefits,
      onServiceHover,
      onServiceLeave,
      onButtonHover,
      onButtonLeave,
      onBenefitHover,
      onBenefitLeave,
      openLearnMoreModal
    }
  }
}
</script>
